import { useEffect, useReducer, useRef, useState } from 'react';
import { Box, Button, IconButton, Stack, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { ENTRIES, ITEM_SIZE, PLAYER_TOP, PLAYER_WIDTH, gameReducer, initialGame } from './checkInRushEngine';
import { ArcadeBurst, ArcadeCabinet, ArcadeResults, ArcadeSprite, ArcadeText, ArcadeVenue } from './CheckInRushArt';
import { arcadeTokens } from '../theme/tokens/arcade';

const STORAGE_KEY = 'attendin.check-in-rush.best';
const SPRITES = ['badge', 'vip', 'qr', 'spam'] as const;
const MESSAGES = [
    'Event not found', 'Venue data missing', 'Attendee database recovering',
    'Check-in terminal offline', 'Saving the event...', '404 detected',
    'Locating venue...', 'Thankfully the attendees are still here',
];
const formatScore = (score: number) => score.toLocaleString('en-GB');
const directionFor = (key: string) => ['ArrowLeft', 'a', 'A'].includes(key) ? -1 : ['ArrowRight', 'd', 'D'].includes(key) ? 1 : 0;

function readBest() {
    try {
        const value = Number(window.localStorage.getItem(STORAGE_KEY));
        return Number.isFinite(value) && value >= 0 ? value : 0;
    } catch {
        return 0;
    }
}

export default function CheckInRush() {
    const theme = useTheme();
    const tokens = arcadeTokens(theme);
    const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
    const [message] = useState(() => Math.floor(Math.random() * MESSAGES.length));
    const [game, dispatch] = useReducer(gameReducer, undefined, () => initialGame(readBest()));
    const board = useRef<HTMLDivElement>(null);
    const playButton = useRef<HTMLButtonElement>(null);
    const keys = useRef(new Set<string>());
    const pointers = useRef(new Map<number, -1 | 1>());
    const playing = game.phase === 'playing';
    const finished = game.phase === 'finished';

    useEffect(() => {
        if (!playing) return;
        let frame = 0;
        const tick = () => {
            const directions = [...keys.current].map(directionFor).concat([...pointers.current.values()]);
            const direction = Number(directions.includes(1)) - Number(directions.includes(-1));
            if (board.current) board.current.dataset.moving = String(direction);
            dispatch({ type: 'frame', now: Date.now(), direction, randomKind: Math.random(), randomX: Math.random() });
            frame = requestAnimationFrame(tick);
        };
        const clearInput = () => {
            keys.current.clear();
            pointers.current.clear();
            if (board.current) board.current.dataset.moving = '0';
        };
        const visibility = () => {
            clearInput();
            dispatch({ type: 'frame', now: Date.now(), direction: 0, randomKind: Math.random(), randomX: Math.random() });
        };
        frame = requestAnimationFrame(tick);
        window.addEventListener('blur', clearInput);
        document.addEventListener('visibilitychange', visibility);
        return () => {
            cancelAnimationFrame(frame);
            clearInput();
            window.removeEventListener('blur', clearInput);
            document.removeEventListener('visibilitychange', visibility);
        };
    }, [playing]);

    useEffect(() => {
        if (playing) board.current?.focus({ preventScroll: true });
        else if (finished) playButton.current?.focus({ preventScroll: true });
    }, [playing, finished]);

    useEffect(() => {
        if (!finished) return;
        try {
            window.localStorage.setItem(STORAGE_KEY, String(Math.max(readBest(), game.best)));
        } catch {
            return;
        }
    }, [finished, game.best]);

    return (
        <Box component="section" aria-labelledby="check-in-rush-heading" sx={{ mb: 4, mx: 'auto', maxWidth: tokens.maxWidth, textAlign: 'left' }}>
            <ArcadeCabinet>
                <Typography id="check-in-rush-heading" variant="h6" component="h2" align="center" sx={{ mb: 2 }}>Check-In Rush</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4,minmax(0,1fr))', gap: 1, mb: 2 }}>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" color="text.secondary">Score</Typography>
                        <Box key={game.score} className="arcade-score" data-testid="rush-score" sx={{ mt: 1 }}><ArcadeText text={formatScore(game.score)} size="number" /></Box>
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" color="text.secondary">Best</Typography>
                        <Box data-testid="rush-best" sx={{ mt: 1 }}><ArcadeText text={formatScore(Math.max(game.best, game.score))} size="number" color={theme.palette.primary.main} /></Box>
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" color="text.secondary">Time</Typography>
                        <Box role="timer" aria-label={`${game.remaining} seconds remaining`} sx={{ mt: 1 }}><ArcadeText text={`${game.remaining}s`} size="number" color={game.remaining <= 10 ? theme.palette.error.dark : theme.palette.text.primary} /></Box>
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" color="text.secondary">Combo</Typography>
                        <Box sx={{ mt: 1 }}><ArcadeText text={`x${game.streak}`} size="number" color={theme.palette.text.primary} /></Box>
                    </Box>
                </Box>
                <Box
                    ref={board}
                    role="group"
                    aria-label="Attendee catcher game"
                    aria-description="Move your organiser with the left and right arrow keys or A and D. Catch attendee badges and VIP passes. Avoid broken QR codes and spam registrations."
                    tabIndex={0}
                    onKeyDown={(event) => {
                        if (!playing || !directionFor(event.key) || event.altKey || event.ctrlKey || event.metaKey) return;
                        event.preventDefault();
                        keys.current.add(event.key);
                    }}
                    onKeyUp={(event) => {
                        if (directionFor(event.key)) { event.preventDefault(); keys.current.delete(event.key); }
                    }}
                    onBlur={(event) => {
                        if (!event.currentTarget.contains(event.relatedTarget)) { keys.current.clear(); pointers.current.clear(); }
                    }}
                    sx={{
                        position: 'relative', height: game.phase === 'ready' ? tokens.idleHeight : tokens.boardHeight, width: 1, overflow: 'hidden', isolation: 'isolate', color: tokens.paper,
                        bgcolor: tokens.screen, border: 1, borderColor: 'divider',
                        borderRadius: 'var(--radius-compact)', boxShadow: tokens.screenShadow,
                        '&:focus-visible': { outline: 'var(--focus-ring)', outlineOffset: 'var(--focus-ring-offset)' },
                        '&::after': {
                            content: '""', position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 4,
                            background: `repeating-linear-gradient(transparent 0, transparent ${tokens.pixel}, ${tokens.scanline} ${tokens.pixel}, ${tokens.scanline} ${theme.spacing(0.5)})`,
                            boxShadow: tokens.screenShadow,
                        },
                    }}
                >
                    <ArcadeVenue player={game.player} reducedMotion={reducedMotion} />
                    <Box aria-hidden="true" sx={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
                        {game.items.map((item) => {
                            return (
                                <Box key={item.id} data-testid="rush-item"
                                    style={{ left: `${(item.x - ITEM_SIZE / 2) * 100}%`, top: `${item.y * 100}%` }} sx={{
                                        position: 'absolute',
                                        width: `${ITEM_SIZE * 100}%`, height: `${ITEM_SIZE * 100}%`, display: 'flex',
                                        alignItems: 'center', justifyContent: 'center',
                                    }}>
                                    <Box className={item.kind === 2 ? 'arcade-qr' : undefined} sx={{ width: tokens.sprite, height: tokens.sprite, maxWidth: 1, maxHeight: 1 }}><ArcadeSprite name={SPRITES[item.kind]} /></Box>
                                    {item.kind === 1 && <Box className="arcade-sparkle" sx={{ position: 'absolute', top: 0, right: 0, width: theme.spacing(1.5), height: theme.spacing(1.5) }}><ArcadeSprite name="sparkle" /></Box>}
                                </Box>
                            );
                        })}
                        <Box sx={{ position: 'absolute', top: `${PLAYER_TOP * 100}%`, width: 1, borderTop: `${tokens.fineBorder} solid`, borderColor: tokens.venue }} />
                        <Box data-testid="rush-player" style={{ left: `${(game.player - PLAYER_WIDTH / 2) * 100}%` }} sx={{
                            position: 'absolute', top: `${PLAYER_TOP * 100}%`,
                            width: `${PLAYER_WIDTH * 100}%`, height: `${ITEM_SIZE * 100}%`,
                            display: 'flex', justifyContent: 'center', alignItems: 'center',
                            borderBottom: `${tokens.border} solid`, borderColor: tokens.violet,
                        }}>
                            <Box className={playing ? 'arcade-organiser arcade-bob' : 'arcade-organiser'} sx={{ position: 'relative', width: tokens.sprite, height: tokens.sprite, maxHeight: 1 }}>
                                <ArcadeSprite name="organiser" className="organiser-frame-1" />
                                <ArcadeSprite name="organiserWalk" className="organiser-frame-2" />
                            </Box>
                        </Box>
                    </Box>
                    {!playing && (
                        <Stack alignItems="center" justifyContent="center" gap={2} sx={{ position: 'absolute', inset: 0, p: 2, bgcolor: tokens.overlay }}>
                            {finished ? (
                                <ArcadeResults score={game.score} best={game.best} saved={game.saved} record={game.record} reducedMotion={reducedMotion} />
                            ) : (
                                <Stack alignItems="center" gap={2} sx={{ width: 1 }}>
                                    <Stack alignItems="center" gap={1}>
                                        <ArcadeText text="Check-in Rush" size="brand" color={tokens.paper} />
                                    </Stack>
                                    <Box aria-hidden="true" sx={{ width: tokens.trophy, height: tokens.trophy }}><ArcadeSprite name="organiser" /></Box>
                                </Stack>
                            )}
                            <Button ref={playButton} className="arcade-control" variant="outlined" aria-label={finished ? 'Play again' : 'Start game'} onClick={() => {
                                dispatch({ type: 'start', now: Date.now(), best: readBest() });
                            }} sx={{ minHeight: theme.spacing(6) }}>
                                <ArcadeText text={finished ? 'Play again' : 'Press start'} />
                            </Button>
                        </Stack>
                    )}
                    {playing && game.caught > 0 && <ArcadeBurst key={game.caught} points={game.lastPoints} player={game.player} streak={game.streak} />}
                    {playing && game.lastPoints < 0 && <Box key={`penalty-${game.caught}`} className="arcade-damage" aria-hidden="true" sx={{ pointerEvents: 'none', position: 'absolute', inset: 0, border: `${tokens.border} solid`, borderColor: tokens.red, bgcolor: tokens.flash }} />}
                    {finished && game.record && <ArcadeBurst points={25} player={0.5} streak={0} victory />}
                </Box>
                <Stack direction="row" gap={2} sx={{ mt: 2 }}>
                    {([-1, 1] as const).map((direction) => (
                        <Tooltip key={direction} title={direction < 0 ? 'Move left' : 'Move right'}>
                            <IconButton aria-label={direction < 0 ? 'Move left' : 'Move right'} aria-disabled={!playing}
                                onPointerDown={(event) => {
                                    if (!playing) return;
                                    event.preventDefault();
                                    event.currentTarget.setPointerCapture(event.pointerId);
                                    pointers.current.set(event.pointerId, direction);
                                }}
                                onPointerUp={(event) => pointers.current.delete(event.pointerId)}
                                onPointerCancel={(event) => pointers.current.delete(event.pointerId)}
                                onLostPointerCapture={(event) => pointers.current.delete(event.pointerId)}
                                onKeyDown={(event) => {
                                    if (playing && [' ', 'Enter'].includes(event.key)) {
                                        event.preventDefault();
                                        keys.current.add(direction < 0 ? 'ArrowLeft' : 'ArrowRight');
                                    }
                                }}
                                onKeyUp={() => keys.current.clear()}
                                onBlur={() => { keys.current.clear(); pointers.current.clear(); }}
                                onContextMenu={(event) => event.preventDefault()}
                                sx={{ flex: 1, minHeight: tokens.buttonHeight, border: 1, borderColor: 'divider', touchAction: 'none', '&[aria-disabled="true"]': { opacity: theme.palette.action.disabledOpacity } }}
                            >
                                <Box sx={{ width: tokens.sprite, height: tokens.sprite }}><ArcadeSprite name={direction < 0 ? 'left' : 'right'} foreground={theme.palette.text.primary} /></Box>
                            </IconButton>
                        </Tooltip>
                    ))}
                </Stack>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4,minmax(0,1fr))', gap: 1, mt: 2 }} aria-label="Entry scores">
                    {ENTRIES.map((entry, index) => <Tooltip key={entry.label} title={`${entry.label}: ${entry.points > 0 ? '+' : ''}${entry.points}`}>
                        <Stack role="img" alignItems="center" gap={1} aria-label={`${entry.label}: ${entry.points > 0 ? '+' : ''}${entry.points}`}>
                            <Box sx={{ width: tokens.sprite, height: tokens.sprite }}><ArcadeSprite name={SPRITES[index]} /></Box>
                            <Typography variant="caption" color="text.secondary">{entry.points > 0 ? '+' : ''}{entry.points}</Typography>
                        </Stack>
                    </Tooltip>)}
                </Box>
                <Typography variant="body2" color="text.secondary" aria-hidden="true" sx={{ mt: 2, minHeight: theme.spacing(6), textAlign: 'center' }}>
                    {MESSAGES[(message + Math.floor((60 - game.remaining) / 12)) % MESSAGES.length]}
                </Typography>
                <Box role="status" sx={visuallyHidden}>
                    {playing ? game.feedback : finished ? `${game.record ? 'New high score. ' : ''}Time up. Final score: ${formatScore(game.score)}. Best score: ${formatScore(game.best)}. Attendees saved: ${game.saved}.` : ''}
                </Box>
            </ArcadeCabinet>
        </Box>
    );
}